// Trykkliste (/service), i startsidens stil (planer/ny-kundesamhandling-plan.md). Trykkjobber
// (Tekstiltrykking) i butikken med søk, status, «søk i alle statuser», sortering og sider, og
// beskrivelsen synlig på raden (før bare ved musepekeren). Under: aktive Klubb-bestillinger (ordrer
// med status AktivKLUBB, trykk/AktiveKlubbbestillinger.jsx) og klubbordre fra nettbutikken (ClubNet.js).
//
// Samme liste med variant="gamle" er «Gammel hardvare» (/gammel-hardvare, under «Mer»): sykkel-,
// ski- og skøyteservicer i den gamle modellen (vinnsel, 28.10). Fases ut og slettes når de er
// ryddet opp; nye servicer ligger i WorkshopService.
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, Valgknapper, feltKlasse, knappSekundar } from './ny-kundesamhandling/Skjema';
import { FARGER, STI } from './ny-kundesamhandling/handlinger';
import Ikon from './ny-kundesamhandling/Ikon';
import formatDate from './hjelpere/formatDate';
import ClubNet from './ClubNet';
import AktiveKlubbbestillinger from './trykk/AktiveKlubbbestillinger';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TRYKK = 'Tekstiltrykking';
const GAMLE_TYPER = ['Skiservice', 'Sykkelservice', 'Skøyteslip'];
// Det som skiller de to listene.
const VARIANTER = {
  trykk: {
    typer: [TRYKK], ikon: 'bunke', farge: 'trykk', tittel: 'Trykkliste', undertittel: 'Trykkjobber og klubbordre i butikken',
    flertall: 'trykkjobber', entall: 'trykkjobb', overskrift: 'Trykkjobber',
  },
  gamle: {
    typer: GAMLE_TYPER, ikon: 'eske', farge: 'info', tittel: 'Gammel hardvare',
    undertittel: 'Sykkel-, ski- og skøyteservicer i det gamle systemet. Fases ut og slettes når de er ryddet opp.',
    flertall: 'servicer', entall: 'service', overskrift: 'Servicer',
  },
};

const STATUSER = [
  { verdi: 'Aktiv', tittel: 'Aktiv' },
  { verdi: 'Avsluttet', tittel: 'Avsluttet' },
];

const KOLONNER = [
  { key: 'serviceid', tittel: 'Nr.' },
  { key: 'Varemerke', tittel: 'Plagg' },
  { key: 'Størrelse', tittel: 'Str.', bred: true },
  { key: 'ferdig', tittel: 'Ferdig', bred: true },
  { key: 'status', tittel: 'Status' },
  { key: 'registrert', tittel: 'Registrert', bred: true },
];

// Registrert-tidspunktet ligger i ObjectId-en (de eldste tjenestene har ikke eget felt).
const fraObjectId = (id) => (id ? new Date(parseInt(String(id).substring(0, 8), 16) * 1000) : null);

// Ferdigdato lagres med klokkeslettet slik det er skrevet inn (som UTC), så den vises i UTC.
function ferdigTekst(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  const dato = `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  return d.getUTCHours() || d.getUTCMinutes() ? `${dato} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}` : dato;
}

const detaljSti = (s) => ({
  Sykkelservice: `/service-details-bike/${s._id}`,
  Skiservice: `/service-details-ski/${s._id}`,
  Skøyteslip: `/service-details-skate/${s._id}`,
  [TRYKK]: `/service-details-club/${s._id}`,
}[s.servicetype] || `/service-details/${s._id}`);

function StatusMerke({ status }) {
  const cls = status === 'Avsluttet' ? 'bg-slate-200 text-slate-800' : 'bg-emerald-100 text-emerald-900';
  return <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold ${cls}`}>{status || 'Aktiv'}</span>;
}

function BetaltMerke({ s }) {
  if (s.servicetype !== TRYKK) return null;
  const tekst = s.toyBetalt && s.trykkBetalt ? 'Betalt' : s.toyBetalt ? 'Tøy betalt' : s.trykkBetalt ? 'Trykk betalt' : null;
  return tekst ? <span className="inline-block whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-0.5 text-sm font-semibold text-sky-900">{tekst}</span> : null;
}

const sorter = (liste, { key, direction }) => {
  if (!key) return liste;
  const r = direction === 'ascending' ? 1 : -1;
  return [...liste].sort((a, b) => {
    if (key === 'serviceid') return r * ((Number(a.serviceid) || 0) - (Number(b.serviceid) || 0));
    if (key === 'registrert') return r * ((a.registrert || 0) - (b.registrert || 0));
    if (key === 'ferdig') return r * ((a.ferdigDato ? new Date(a.ferdigDato) : 0) - (b.ferdigDato ? new Date(b.ferdigDato) : 0));
    return r * String(a[key] || '').toLowerCase().localeCompare(String(b[key] || '').toLowerCase(), 'nb');
  });
};

function Tabell({ navn, rader, sortConfig, onSort, visType = false }) {
  const navigate = useNavigate();
  return (
    <div tabIndex={0} role="region" aria-label={`${navn}, kan rulles sidelengs`}
      className="overflow-x-auto rounded-2xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
      <table className="min-w-full text-left">
        <caption className="sr-only">Trykk på en kolonneoverskrift for å sortere.</caption>
        <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
          <tr>
            {KOLONNER.map((k) => (
              <th key={k.key} scope="col" aria-sort={sortConfig.key === k.key ? sortConfig.direction : undefined}
                className={`px-2 py-1.5 ${k.bred ? 'hidden md:table-cell' : ''}`}>
                <button type="button" onClick={() => onSort(k.key)}
                  className="inline-flex min-h-[2.5rem] items-center gap-1 rounded-lg px-2 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600">
                  {k.tittel}
                  {sortConfig.key === k.key && <Ikon navn="ned" className={`w-4 h-4 ${sortConfig.direction === 'ascending' ? 'rotate-180' : ''}`} />}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 text-slate-800">
          {rader.map((s) => (
            <tr key={s._id} className="cursor-pointer align-top hover:bg-violet-50/50" onClick={() => navigate(detaljSti(s))}>
              <td className="px-3 py-1.5 font-mono text-sm">
                <Link to={detaljSti(s)} onClick={(e) => e.stopPropagation()} className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
                  {s.serviceid}
                </Link>
              </td>
              <td className="min-w-[12rem] px-3 py-1.5">
                {visType && <span className="block text-sm font-semibold text-slate-600">{s.servicetype}</span>}
                <span className="block font-semibold [overflow-wrap:anywhere]">{[s.Varemerke, s.Produkt].filter(Boolean).join(' ') || 'Uten navn'}</span>
                <span className="block text-sm text-slate-600 md:hidden">{[s.Størrelse, s.Farge, ferdigTekst(s.ferdigDato) && `ferdig ${ferdigTekst(s.ferdigDato)}`].filter(Boolean).join(' · ')}</span>
                {s.Beskrivelse && <span className="block text-sm text-slate-700 line-clamp-2 [overflow-wrap:anywhere]">{s.Beskrivelse}</span>}
              </td>
              <td className="hidden px-3 py-1.5 md:table-cell">{[s.Størrelse, s.Farge].filter(Boolean).join(' · ')}</td>
              <td className="hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell">{ferdigTekst(s.ferdigDato)}</td>
              <td className="px-3 py-1.5">
                <span className="flex flex-wrap gap-1"><StatusMerke status={s.status} /><BetaltMerke s={s} /></span>
              </td>
              <td className="hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell">{s.registrert ? formatDate(s.registrert) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceList({ variant = 'trykk' }) {
  const v = VARIANTER[variant] || VARIANTER.trykk;
  const gamleListe = variant === 'gamle';
  const [alle, setAlle] = useState([]); // alle tjenester i butikken, alle statuser
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');
  const [sok, setSok] = useState('');
  const [status, setStatus] = useState('Aktiv');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [side, setSide] = useState(1);
  const [perSide, setPerSide] = useState(25);
  const [sokAlleStatuser, setSokAlleStatuser] = useState(() => Cookies.get('searchAllStatuses') === 'true');
  const butikkid = parseInt(Cookies.get('butikkid'), 10) || null;

  useEffect(() => {
    const hent = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/services`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setAlle(data.filter((s) => s.butikkid === butikkid).map((s) => ({ ...s, registrert: fraObjectId(s._id) })));
        setFeil('');
      } catch (e) {
        console.error('Feil ved henting av tjenester:', e.message);
        setFeil(`Klarte ikke å hente ${v.flertall}. Last siden på nytt om litt.`);
      } finally {
        setLastet(true);
      }
    };
    hent();
  }, [butikkid]); // eslint-disable-line react-hooks/exhaustive-deps

  const treff = (s, q) => [s.serviceid, s.Varemerke, s.Produkt, s.Størrelse, s.Farge, s.Beskrivelse, s.ansatt]
    .some((v) => v != null && String(v).toLowerCase().includes(q));

  const velg = (typer) => {
    const avType = alle.filter((s) => typer.includes(s.servicetype));
    const q = sok.trim().toLowerCase();
    const grunnlag = q && sokAlleStatuser ? avType : avType.filter((s) => (s.status || 'Aktiv') === status);
    return sorter(q ? grunnlag.filter((s) => treff(s, q)) : grunnlag, sortConfig);
  };

  const trykk = useMemo(() => velg(v.typer), [alle, sok, status, sokAlleStatuser, sortConfig, v]); // eslint-disable-line react-hooks/exhaustive-deps
  const aktiveTrykk = alle.filter((s) => v.typer.includes(s.servicetype) && (s.status || 'Aktiv') === 'Aktiv').length;

  const sorterPa = (key) => setSortConfig((c) => ({ key, direction: c.key === key && c.direction === 'ascending' ? 'descending' : 'ascending' }));

  const totalt = trykk.length;
  const sider = Math.max(1, Math.ceil(totalt / perSide));
  const gjeldende = Math.min(side, sider);
  const fra = (gjeldende - 1) * perSide;
  const visning = trykk.slice(fra, fra + perSide);

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake />

      <Sidetopp ikon={v.ikon} farge={v.farge} tittel={v.tittel} undertittel={v.undertittel}>
        {!gamleListe && (
        <Link to={`${STI}/trykk`}
          className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 border-violet-300 bg-violet-50 px-3 py-1.5 font-bold text-slate-900 transition hover:bg-violet-100 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${FARGER.trykk.ikon}`}>
            <Ikon navn="pluss" className="w-6 h-6" />
          </span>
          Registrer trykk
        </Link>
        )}
      </Sidetopp>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      <section aria-labelledby="tl-trykk" className="space-y-3">
        <h2 id="tl-trykk" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
          {v.overskrift}
          {lastet && <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-sm font-semibold text-violet-900">{aktiveTrykk} aktive</span>}
          {gamleListe && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-semibold text-amber-900">Fases ut</span>}
        </h2>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div>
            <label htmlFor="tl-sok" className="mb-1 block font-semibold text-slate-800">Søk i {gamleListe ? 'servicene' : 'trykkjobbene'}</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"><Ikon navn="sok" className="w-6 h-6" /></span>
              <input id="tl-sok" type="search" inputMode="search" autoComplete="off" value={sok}
                onChange={(e) => { setSok(e.target.value); setSide(1); }}
                placeholder={gamleListe ? 'Nr., merke, produkt, størrelse, farge, beskrivelse eller ansatt' : 'Nr., merke, plagg, størrelse, farge, beskrivelse eller ansatt'}
                aria-describedby="tl-sok-status" className={`${feltKlasse} pl-12`} />
            </div>
            <p id="tl-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
              {sok.trim() && lastet ? `${totalt} ${totalt === 1 ? v.entall : v.flertall} passer` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <Valgknapper id="tl-status" etikett="Status" valg={STATUSER} verdi={status} onVelg={(v) => { setStatus(v); setSide(1); }} />
            <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 font-medium text-slate-800">
              <input type="checkbox" checked={sokAlleStatuser} className="h-5 w-5 cursor-pointer"
                onChange={() => { const ny = !sokAlleStatuser; setSokAlleStatuser(ny); Cookies.set('searchAllStatuses', String(ny)); setSide(1); }} />
              Søk i alle statuser
            </label>
          </div>
        </div>

        {!lastet ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter {v.flertall} …</p>
        ) : totalt === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
            {sok.trim() ? `Ingen ${v.flertall} passer søket.` : `Ingen ${v.flertall} med denne statusen.`}
          </p>
        ) : (
          <Tabell navn={v.overskrift} rader={visning} sortConfig={sortConfig} onSort={sorterPa} visType={gamleListe} />
        )}

        {totalt > 0 && (
          <nav aria-label="Sider" className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <label htmlFor="tl-per-side" className="font-medium text-slate-800">Vis per side</label>
              <select id="tl-per-side" value={perSide} onChange={(e) => { setPerSide(parseInt(e.target.value, 10)); setSide(1); }}
                className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3">
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <p className="text-slate-700">Viser {fra + 1}–{Math.min(fra + perSide, totalt)} av {totalt}</p>
            {sider > 1 && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setSide(gjeldende - 1)} disabled={gjeldende === 1} className={knappSekundar}>Forrige</button>
                {[...Array(sider)].map((_, i) => (
                  <button key={i} type="button" onClick={() => setSide(i + 1)} aria-current={gjeldende === i + 1 ? 'page' : undefined} aria-label={`Side ${i + 1}`}
                    className={`min-h-[2.25rem] min-w-[2.25rem] rounded-xl border-2 font-semibold focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
                      gjeldende === i + 1 ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'}`}>
                    {i + 1}
                  </button>
                ))}
                <button type="button" onClick={() => setSide(gjeldende + 1)} disabled={gjeldende === sider} className={knappSekundar}>Neste</button>
              </div>
            )}
          </nav>
        )}
      </section>

      {!gamleListe && <AktiveKlubbbestillinger butikkid={butikkid} />}
      {!gamleListe && <ClubNet />}
    </div>
  );
}

export default ServiceList;
